import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Typography, Stack, Chip, Divider, Alert, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, CircularProgress } from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AssignmentApi, { AssignmentClient, ClientDetail } from "../api/AssignmentApi";
import CatalogBrowser from "./CatalogBrowser";

function fmtDate(iso: string, lang: string) {
  return new Intl.DateTimeFormat(lang, { day: "numeric", month: "short", year: "numeric" }).format(new Date(iso));
}

// Master-detail cliëntenbeheer voor psychologen. Master: de cliënten met een
// afspraak. Detail: hun afspraken + het huiswerk dat DEZE psycholoog toewees,
// plus de toewijs-actie via de catalogus. Privacy-lijn: nooit de antwoorden of
// persoonlijke notities van de cliënt — enkel "toegewezen op [datum]" + de
// eigen note van de psycholoog.
export default function Clients() {
  const { t, i18n } = useTranslation();
  const [clients, setClients] = useState<AssignmentClient[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState<ClientDetail | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);

  useEffect(() => { AssignmentApi.clients().then(setClients).catch(() => {}); }, []);

  const loadDetail = (id: string) => {
    setSelectedId(id);
    setDetail(null);
    AssignmentApi.clientDetail(id).then(setDetail).catch(() => {});
  };
  const reloadDetail = () => selectedId && AssignmentApi.clientDetail(selectedId).then(setDetail).catch(() => {});

  const removeAssignment = async (id: string) => { await AssignmentApi.remove(id); reloadDetail(); };

  if (clients.length === 0) return <Typography sx={{ color: "text.secondary" }}>{t("assign.noClients")}</Typography>;

  const fmtAppt = new Intl.DateTimeFormat(i18n.language, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

  const master = (
    <Stack spacing={0.5}>
      {clients.map((c) => {
        const active = c.id === selectedId;
        return (
          <Box key={c.id} role="button" onClick={() => loadDetail(c.id)}
            sx={{ p: 1.5, borderRadius: "var(--radius-md)", cursor: "pointer", border: "1px solid", borderColor: active ? "primary.main" : "var(--border)", bgcolor: active ? "var(--primary-50)" : "var(--surface)", "&:hover": { bgcolor: active ? "var(--primary-50)" : "var(--surface-alt)" } }}>
            <Typography sx={{ fontWeight: 600, fontSize: ".95rem" }}>{c.name || c.email}</Typography>
            {c.name && <Typography sx={{ fontSize: ".82rem", color: "text.secondary" }}>{c.email}</Typography>}
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
              {c.clientIsMinor && <Chip size="small" color="warning" label={t("assign.minorFlag")} />}
              {c.intakeCrisisSignal && <Chip size="small" color="error" label={t("assign.crisisFlag")} />}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );

  const detailPane = !selectedId ? (
    <Typography sx={{ color: "text.secondary" }}>{t("assign.selectClient")}</Typography>
  ) : !detail ? (
    <Box sx={{ py: 4, display: "grid", placeItems: "center" }}><CircularProgress size={28} sx={{ color: "primary.main" }} /></Box>
  ) : (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h3" sx={{ fontSize: "1.2rem", mb: 0.5 }}>{detail.client.name || detail.client.email}</Typography>
        {detail.client.name && <Typography sx={{ fontSize: ".85rem", color: "text.secondary" }}>{detail.client.email}</Typography>}
        {detail.client.clientIsMinor && <Alert severity="warning" sx={{ mt: 1.5 }}>{t("assign.minorNote")}</Alert>}
        {detail.client.intakeCrisisSignal && <Alert severity="error" sx={{ mt: 1.5 }}>{t("assign.crisisNote")}</Alert>}
      </Box>

      {/* (a) Afspraken van deze cliënt — neutrale projectie, geen inhoud. */}
      <Box>
        <Typography variant="h3" sx={{ fontSize: "1.05rem", mb: 1 }}>{t("assign.apptsBlock")}</Typography>
        {detail.appointments.length === 0 ? (
          <Typography sx={{ color: "text.secondary", fontSize: ".9rem" }}>{t("dashboard.noAppointments")}</Typography>
        ) : (
          <Stack divider={<Divider />} spacing={0}>
            {detail.appointments.map((a) => (
              <Box key={a._id} component={RouterLink} to={`/afspraak/${a._id}`} sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.25, textDecoration: "none", color: "text.primary" }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: ".92rem" }}>{t(`booking.type.${a.type}`)}</Typography>
                  <Typography sx={{ fontSize: ".85rem", color: "text.secondary", textTransform: "capitalize" }}>{fmtAppt.format(new Date(a.start))}</Typography>
                </Box>
                <Chip size="small"
                  label={t(`appointment.status${a.status === "confirmed" ? "Confirmed" : a.status === "cancelled" ? "Cancelled" : "Pending"}`)}
                  color={a.status === "confirmed" ? "success" : a.status === "cancelled" ? "default" : "warning"} />
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      {/* (b) Toegewezen huiswerk — enkel "toegewezen op" + eigen note. */}
      <Box>
        <Typography variant="h3" sx={{ fontSize: "1.05rem", mb: 1 }}>{t("assign.homeworkBlock")}</Typography>
        <Alert severity="info" sx={{ mb: 1.5 }}>{t("assign.privacyBanner")}</Alert>
        {detail.assignments.length === 0 ? (
          <Typography sx={{ color: "text.secondary", fontSize: ".9rem", mb: 1.5 }}>{t("assign.noHomework")}</Typography>
        ) : (
          <Stack divider={<Divider />} spacing={0} sx={{ mb: 1.5 }}>
            {detail.assignments.map((a) => (
              <Box key={a.id} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, py: 1.25 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: ".92rem" }}>
                    <strong>{a.kind === "selftest" ? a.selfTestTitle : a.courseTitle}</strong>{a.moduleTitle ? ` · ${a.moduleTitle}` : ""}
                    {a.kind === "selftest" && <Chip size="small" color="info" label={t("assign.testChip")} sx={{ ml: 1 }} />}
                  </Typography>
                  <Typography sx={{ fontSize: ".82rem", color: "text.secondary" }}>{t("assign.assignedOn", { date: fmtDate(a.createdAt, i18n.language) })}</Typography>
                  {a.note && <Typography sx={{ fontSize: ".85rem", color: "text.secondary" }}>“{a.note}”</Typography>}
                </Box>
                <IconButton size="small" aria-label={t("assign.remove")} onClick={() => removeAssignment(a.id)}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton>
              </Box>
            ))}
          </Stack>
        )}
        {/* (c) De toewijs-actie via de catalogus. */}
        <Button variant="contained" onClick={() => setAssignOpen(true)}>{t("assign.assignAnother")}</Button>
      </Box>
    </Stack>
  );

  const selected = clients.find((c) => c.id === selectedId);

  return (
    <>
      {clients.some((c) => c.clientIsMinor) && <Alert severity="warning" sx={{ mb: 2 }}>{t("assign.minorNote")}</Alert>}
      <Box sx={{ display: { md: "grid" }, gridTemplateColumns: { md: "260px 1fr" }, gap: { md: 3 }, alignItems: "start" }}>
        <Box sx={{ mb: { xs: 2, md: 0 } }}>{master}</Box>
        <Box>{detailPane}</Box>
      </Box>

      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>{t("assign.libraryTitle")}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>{t("assign.privacyBanner")}</Alert>
          <CatalogBrowser assignTarget={selected ? {
            clientName: selected.name || selected.email,
            onAssign: async (sel) => {
              await AssignmentApi.create({ clientUserId: selected.id, ...sel });
              setAssignOpen(false);
              reloadDetail();
            },
          } : undefined} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>{t("catalog.close")}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
